import { Breadcrumbs, Link, Typography } from '@mui/material';
import { capitalCase } from 'change-case';
import { Link as RouterLink } from 'gatsby';
import type { Crumb } from 'gatsby-plugin-breadcrumb';
import React from 'react';

import Icon from '@components/Icon/Icon';

interface BreadcrumbProps {
  /**
   * Breadcrumbs to display
   */
  crumbs: Crumb[];
  /**
   * Index in `crumbs` to start at. Useful in sub-menu situations. E.g. with gatsby-plugin-breadcrumb the first element is always the crumb for "Home"
   * @default 1
   */
  startIndex?: number;
}

function cleanName(str: string) {
  return capitalCase(str);
}

/**
 * Breadcrumbs are a list of links that help visualize a page's location within a site's hierarchical structure, it allows navigation up to any of the ancestors
 * 
 * Usage:
 * 
 * ```
 * // my-page.tsx
 * 
 * // Import from gatsby-plugin-breadcrumb
 * const { crumbs } = useBreadcrumb({
    location,
    crumbLabel: 'Manage Users',
  });
  // OR using Gatsby pageContext from PageProps
  const { breadcrumb } = pageContext;
  const { crumbs } = breadcrumb;
  ...
  return <Breadcrumb crumbs={crumbs} />
 * ```
 */
export default function Breadcrumb(props: BreadcrumbProps) {
  const { crumbs, startIndex = 1 } = props;
  const displayedCrumbs = crumbs.slice(startIndex);
  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      separator={<Icon type="fad" name="chevron-right" />}
    >
      {displayedCrumbs.map(({ crumbLabel, pathname }, index) => {
        const isLast = index === displayedCrumbs.length - 1;
        const color = isLast ? 'text.primary' : 'inherit';
        const underline = isLast ? 'hover' : 'always';
        const label = cleanName(crumbLabel);
        if (isLast) {
          return (
            <Typography color={color} key={index} variant="subtitle2">
              {label}
            </Typography>
          );
        }
        return (
          <Link
            color={color}
            component={RouterLink}
            key={index}
            to={pathname}
            variant="subtitle2"
            underline={underline}
          >
            {label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}
